import React, { useEffect, useState, useRef, useContext } from "react";
import { Container, Button, Row, Col, Alert, Spinner } from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import CommonForm from "../../../../Components/Common/CommonForm";
import SectionCard from "../../../../Components/Common/SectionCard";
import FilePreviewModal from "../../../../Components/Common/FilePreviewModal";
import FullPageLoader from "../../../../Components/Common/FullPageLoader";
import formStyles from "../../../../assets/scss/common/formStyle.module.scss";
import styles from "../css/addDropoutVolunteer.module.scss";
import addVolunteerStyle from "../../MasterVolunteer/css/volunteer.module.scss";
import {
  getSubjectFields,
  getDropoutTypeFields,
  getDropoutDetailsFields,
  getDropoutReasonFields,
  // getReplacementFields,
} from "../helper/dropoutvolunteer.constant";
import { DropoutVolunteerFormValues } from "../helper/dropoutvolunteer.types";
import { DROPOUT_VOLUNTEER_PATHS, SUBJECT_PATHS } from "Routes/paths";
import { useVolunteerService } from "modules/Volunteer/MasterVolunteer/api/useVolunteerQuery";
import { DROPOUT_REASONS_OPTIONS, DROPOUT_TYPE_OPTIONS, loadCodelistsDetailId } from "api/modules/common.option";
import { formatDateSplit, showToastMessage } from "common/service/CommonService";
import { BreadcrumbContext } from "Layouts/Header";
import { useDropoutVolunteerService } from "../api/useDropoutVolunteerQuery";
import { useStudyDetailsService } from "hooks/useCommonQuery";
import { queryClient } from "api/queryClient";
import Reason from "common/utilities/Reason/Reason";
import TruncatedText from "Components/Common/TruncatedText";
import CommonFileProgress from "../../../../Components/Common/CommonFileUpload/CommonFileProgress";

const parseSizeToBytes = (sizeStr: any): number => {
  if (!sizeStr) return 0;
  if (typeof sizeStr === "number") return sizeStr;
  
  const str = String(sizeStr).toLowerCase().trim();
  const val = parseFloat(str);
  if (isNaN(val)) return 0;

  if (str.includes("gb")) return val * 1024 * 1024 * 1024;
  if (str.includes("mb")) return val * 1024 * 1024;
  if (str.includes("kb")) return val * 1024;
  return val; 
};

const getRomanNumeral = (num: number): string => {
  const romanMap: { [key: number]: string } = {
    1: "I", 2: "II", 3: "III", 4: "IV", 5: "V", 6: "VI", 7: "VII", 8: "VIII", 9: "IX", 10: "X"
  };
  return romanMap[num] || String(num);
};

const AddDropoutVolunteer: React.FC = () => {
  // -------------------------------------------------------------------------------------------
  // 1. Hooks & Context
  // -------------------------------------------------------------------------------------------
  const navigate = useNavigate();
  const { id: editId } = useParams<{ id: string }>();
  const breadcrumbContext = useContext(BreadcrumbContext);

  // Local Storage Variables
  const selectedsubject = localStorage.getItem("subjectno");
  const selectedvolunteerid = Number(localStorage.getItem("activevolunteerid"));
  const selectedstudycode = localStorage.getItem("studycode");
  const selectedstudyid = Number(localStorage.getItem("activestudyid"));

  // -------------------------------------------------------------------------------------------
  // 2. Queries & Mutations
  // -------------------------------------------------------------------------------------------
  const { SelectQuery: DropoutSelectQuery, InsertMutation, UpdateMutation } = useDropoutVolunteerService({
    volunteerdropoutid: editId ? Number(editId) : null,
  });
  const dropoutRecord = DropoutSelectQuery?.data?.data?.[0];

  const volunteerIdToFetch = editId && dropoutRecord ? Number(dropoutRecord.volunteerid) : selectedvolunteerid;
  const { SelectQuery: VolunteerSelectQuery } = useVolunteerService({
    id: volunteerIdToFetch,
  });
  const volunteerDetails = VolunteerSelectQuery?.data?.data[0];
  const profileurl = localStorage.getItem("filebaseUrl") + "/" + volunteerDetails?.profileimg;

  const studyIdToFetch = editId && dropoutRecord ? Number(dropoutRecord.studyid) : selectedstudyid;
  const validStudyId = studyIdToFetch && !isNaN(studyIdToFetch) ? studyIdToFetch : undefined;
  const { SelectQuery: StudySelectQuery } = useStudyDetailsService(
    undefined,
    undefined,
    validStudyId
  );
  const studyDetails = StudySelectQuery?.data?.data?.[0];

  const periodsList = studyDetails?.periods || [];
  const studyStageOptions = periodsList.map((p: any) => ({
    label: `Period ${getRomanNumeral(Number(p.period_no))}`,
    value: `Period ${getRomanNumeral(Number(p.period_no))}`,
  }));

  // -------------------------------------------------------------------------------------------
  // 3. Form Setup
  // -------------------------------------------------------------------------------------------
  const {
    control,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useForm<DropoutVolunteerFormValues>({
    defaultValues: {
      studycode: selectedstudycode,
      subjectno: selectedsubject,
      subjectinitial: volunteerDetails?.initials,
      dropouttype: "Medical / AE",
      aereferenceno: volunteerDetails?.adverseeventnumber,
      dropoutdate: "",
      studystage: "",
      effectivedate: "",
      dropoutreason: "",
      detailedremarks: "",
      // replacementrequired: "No",
      // replacementsubjectid: "",
    } as any,
    mode: "onBlur",
  });

  const dropoutType = watch("dropouttype");
  const dropoutDateValue = watch("dropoutdate");
  const effectiveDateValue = watch("effectivedate");

  // -------------------------------------------------------------------------------------------
  // 4. States
  // -------------------------------------------------------------------------------------------
  const [isDragging, setIsDragging] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [existingAttachments, setExistingAttachments] = useState<any[]>([]);
  const [deletedAttachmentIds, setDeletedAttachmentIds] = useState<number[]>([]);
  const [isReasonModal, setIsReasonModal] = useState<boolean>(false);
  const [pendingUpdatePayload, setPendingUpdatePayload] = useState<any | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const [previewFileUrl, setPreviewFileUrl] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const [optionsLoaded, setOptionsLoaded] = useState(false);

  // -------------------------------------------------------------------------------------------
  // 5. Effects
  // -------------------------------------------------------------------------------------------
  useEffect(() => {
    document.title = `${editId ? "Edit" : "Add"} Dropout Record | ZENTIXS ORBIT`;
  }, [editId]);

  useEffect(() => {
    editId ? (breadcrumbContext?.setBreadcrumbItems?.([
      { text: "Dropout Volunteer", link: DROPOUT_VOLUNTEER_PATHS.LIST },
      { text: "Edit Dropout Record" },
    ])) : (breadcrumbContext?.setBreadcrumbItems?.([
      { text: "Subject Detail", link: SUBJECT_PATHS.INFO },
      { text: "Add Dropout Record" },
    ]));

    return () => breadcrumbContext?.setBreadcrumbItems?.([]);
  }, [editId]);

  useEffect(() => {
    if (editId && dropoutRecord) {
      setExistingAttachments(dropoutRecord.attachments || []);
      reset({
        studycode: dropoutRecord.studycode,
        subjectno: dropoutRecord.subjectno,
        subjectinitial: dropoutRecord.initials,
        dropouttype: dropoutRecord.dropouttype,
        aereferenceno: dropoutRecord.aereferenceno || "",
        dropoutdate: dropoutRecord.dropoutdate || "",
        studystage: dropoutRecord.studystage || "",
        effectivedate: dropoutRecord.effectivedate || "",
        dropoutreason: dropoutRecord.dropoutreason,
        detailedremarks: dropoutRecord.remarks || "",
      } as any);
    } else {
      reset({
        studycode: selectedstudycode || "",
        subjectno: selectedsubject || "",
        subjectinitial: volunteerDetails?.initials || "",
        dropouttype: "Medical / AE",
        aereferenceno: volunteerDetails?.adverseeventnumber,
        dropoutdate: "",
        studystage: "",
        effectivedate: "",
        dropoutreason: "",
        detailedremarks: "",
        // replacementrequired: "No",
        // replacementsubjectid: "",
      } as any);
    }
  }, [editId, dropoutRecord, reset, selectedstudycode, selectedsubject, volunteerDetails]);

  useEffect(() => {
    const load = async () => {
      const result = await loadCodelistsDetailId([
        "DROPOUT_TYPE_OPTIONS",
        "DROPOUT_REASONS_OPTIONS"
      ]);
      const allLoaded = Object.values(result).every((flag) => flag);
      if (!allLoaded) {
        showToastMessage("error", "Some dropdowns failed to load");
      }
      setOptionsLoaded(true);
    };
    load();
  }, []);

  useEffect(() => {
    if (optionsLoaded && DROPOUT_TYPE_OPTIONS.length > 0) {
      const medicalAeOption = DROPOUT_TYPE_OPTIONS.find(
        (opt) => opt.label === "Medical / AE"
      );
      if (medicalAeOption) {
        if (dropoutType === "Medical / AE") {
          setValue("dropouttype", medicalAeOption.value as any);
        } else if (String(dropoutType) === String(medicalAeOption.value)) {
          const defaultAeNo = editId && dropoutRecord ? dropoutRecord.aereferenceno : volunteerDetails?.adverseeventnumber;
          setValue("aereferenceno", defaultAeNo || "");
        } else {
          setValue("aereferenceno", "");
        }
      }
    }
  }, [optionsLoaded, dropoutType, setValue, editId, dropoutRecord, volunteerDetails]);

  useEffect(() => {
    if (volunteerDetails?.initials) {
      setValue("subjectinitial", volunteerDetails.initials);
    }
  }, [volunteerDetails, setValue]);

  useEffect(() => {
    if (dropoutDateValue && effectiveDateValue) {
      const start = new Date(dropoutDateValue);
      const end = new Date(effectiveDateValue);
      if (end < start) {
        setValue("effectivedate", "");
      }
    }
  }, [dropoutDateValue, effectiveDateValue, setValue]);

  // -------------------------------------------------------------------------------------------
  // 6. Data Processing & Derived States
  // -------------------------------------------------------------------------------------------
  const selectedTypeLabel = DROPOUT_TYPE_OPTIONS.find(
    (opt) => String(opt.value) === String(dropoutType) || opt.label === dropoutType
  )?.label || (dropoutType ? String(dropoutType) : null);

  const isMedicalAE = selectedTypeLabel === "Medical / AE";


  // -------------------------------------------------------------------------------------------
  // 7. Handlers & Actions
  // -------------------------------------------------------------------------------------------

  const MAX_TOTAL_SIZE = 20 * 1024 * 1024; // 20 MB limit

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const currentTotal = uploadedFiles.reduce((sum, f) => sum + f.size, 0);
    const incomingTotal = files.reduce((sum, f) => sum + f.size, 0);
    if (currentTotal + incomingTotal > MAX_TOTAL_SIZE) {
      showToastMessage("error","Total attachment size cannot exceed 20 MB.");
      e.target.value = "";
      return;
    }
    setUploadedFiles((prev) => [...prev, ...files]);
    e.target.value = "";
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    const currentTotal = uploadedFiles.reduce((sum, f) => sum + f.size, 0);
    const incomingTotal = files.reduce((sum, f) => sum + f.size, 0);
    if (currentTotal + incomingTotal > MAX_TOTAL_SIZE) {
      showToastMessage("error","Total attachment size cannot exceed 20 MB.");
      return;
    }
    setUploadedFiles((prev) => [...prev, ...files]);
  };

  const removeFile = (index: number) =>
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));

  const handleDeleteExisting = (attachmentId: number) => {
    setDeletedAttachmentIds((prev) => [...prev, attachmentId]);
    setExistingAttachments((prev) =>
      prev.filter((a) => Number(a.dropoutattachmentid) !== Number(attachmentId))
    );
  };

  const handlePreview = (file: any) => {
 const API_BASE_URL =  localStorage.getItem("baseUrl") || process.env.REACT_APP_BASE_URL
      const baseUrl = API_BASE_URL?.replace("/api", "") || "";

    // NEW FILE (File object)
    if (file instanceof File) {
      setPreviewFile(file);
      setPreviewFileUrl(null);
      setPreviewOpen(true);
      return;
    }

    // EXISTING FILE (API object)
    const path = file?.filepath || file?.path;

    if (!path) {
      console.error("Invalid file object:", file);
      return;
    }

    const finalUrl = path.startsWith("http")
      ? path
      : `${baseUrl}${path.startsWith("/") ? path : "/" + path}`;

    setPreviewFile(null);
    setPreviewFileUrl(finalUrl);
    setPreviewOpen(true);
  };

  const btnReasonClick = (reasonPayload: { reason: string }) => {
    if (!pendingUpdatePayload || !editId) return;

    UpdateMutation.mutate(
      {
        ...pendingUpdatePayload,
        updatereason: reasonPayload.reason,
      },
      {
        onSuccess: (res) => {
          queryClient.invalidateQueries({ queryKey: ["dropout-volunteer-select-all"] });
          showToastMessage("success", res?.message || "Dropout record updated successfully");
          setIsReasonModal(false);
          setPendingUpdatePayload(null);
          navigate(DROPOUT_VOLUNTEER_PATHS.LIST);
        },
        onError: (err: any) => {
          console.error("Update Error:", err);
          showToastMessage("error", err?.message || err || "Update failed");
        },
      }
    );
  };

  const onSubmit = (values: DropoutVolunteerFormValues) => {
    const payload = {
      ...values,
      volunteerid: editId && dropoutRecord ? Number(dropoutRecord.volunteerid) : selectedvolunteerid,
      studyid: editId && dropoutRecord ? Number(dropoutRecord.studyid) : selectedstudyid,
      attachments: uploadedFiles,
    };
    if (editId) {
      const updatePayload = {
        ...payload,
        volunteerdropoutid: Number(editId),
        deletedAttachmentIds: deletedAttachmentIds,
      };
      setPendingUpdatePayload(updatePayload);
      setIsReasonModal(true);
    } else {
      InsertMutation.mutate(payload as any, {
        onSuccess: (response: any) => {
          queryClient.invalidateQueries({ queryKey: ["dropout-volunteer-select-all"] });
          queryClient.invalidateQueries({ queryKey: ["verification-notification-count"] });
          showToastMessage("success", response?.message || "Dropout Record Submitted successfully!");
          navigate(SUBJECT_PATHS.INFO);
        },
        onError: (err: any) => {
          console.error("Error", err);
          showToastMessage("error", err || "Failed to submit Dropout Record");
        },
      });
    }
  };


  const handleClear = () => {
    reset();
    setUploadedFiles([]);
  };

  if (DropoutSelectQuery?.isFetching || VolunteerSelectQuery?.isFetching || StudySelectQuery?.isFetching) {
    return (
      <FullPageLoader message="Fetching details..." />
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        {/* HEADER */}
        <div className="d-flex justify-content-between align-items-center mt-1 mb-3 px-2">
          <div>
            <div className="d-flex align-items-center gap-2 mb-1">
              <h5 className="mb-0 fw-bold text-dark">{editId ? "Edit" : "Add"} Dropout Record</h5>
            </div>
            <p className="text-muted fs-13 mb-0">
              Record and manage volunteer study dropouts due to adverse events, voluntary withdrawal, or investigator decisions.
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          {/* SUBJECT IDENTIFICATION */}
          <SectionCard
            icon="ri-user-search-line"
            title="Subject Identification"
            subtitle="Search and confirm the subject being dropped out"
          >
            <CommonForm
              fields={getSubjectFields()}
              control={control}
              errors={errors}
              columns={3}
              setValue={setValue}
            />

            {volunteerDetails && (
              <div className={styles.subjectInfoStrip}>
                <img src={profileurl} alt={volunteerDetails.initials} className={styles.subjectAvatar} />
                <div className={styles.metaGrid}>
                  <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>Age / Gender</span>
                    <span className={styles.metaValue}>
                      {volunteerDetails.age} / {volunteerDetails.gendertext}
                    </span>
                  </div>
                  <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>Blood Group</span>
                    <span className={styles.metaValue}>{volunteerDetails.bloodgrouptext || "—"}</span>
                  </div>
                  <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>Weight</span>
                    <span className={styles.metaValue}>{volunteerDetails.weight || "—"}</span>
                  </div>
                  <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>Height</span>
                    <span className={styles.metaValue}>{volunteerDetails.height || "—"}</span>
                  </div>
                  <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>BMI</span>
                    <span className={styles.metaValue}>{volunteerDetails.bmi || "—"}</span>
                  </div>
                  <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>Status</span>
                    <span className={`${styles.metaValue} ${styles.statusActive}`}>{volunteerDetails.status}</span>
                  </div>
                </div>
              </div>
            )}
          </SectionCard>

          {/* DROPOUT DETAILS */}
          <SectionCard
            icon="ri-logout-box-r-line"
            title="Dropout Details"
            subtitle="Specify the type, reason, stage, and dates for this dropout"
          >
            <CommonForm
              fields={getDropoutTypeFields(DROPOUT_TYPE_OPTIONS)}
              control={control}
              errors={errors}
              columns={1}
              setValue={setValue}
            />

            {isMedicalAE && (
              <div className="mt-3 mb-2">
                <Alert color="danger" className="border-0 bg-light-danger text-danger d-flex flex-column gap-1 p-3">
                  <div className="fw-semibold">
                    <i className="ri-error-warning-line me-2" />
                    Medical / Adverse Event Dropout Selected
                  </div>
                  <div className="small text-muted">
                    Please provide the linked AE reference number for traceability.
                  </div>
                </Alert>
              </div>
            )}

            <div className="mt-3">
              <CommonForm
                fields={getDropoutDetailsFields(selectedTypeLabel, dropoutDateValue, studyStageOptions)}
                control={control}
                errors={errors}
                columns={isMedicalAE ? 4 : 3}
                setValue={setValue}
              />
            </div>

            <div className="mt-3">
              <CommonForm
                fields={getDropoutReasonFields(DROPOUT_REASONS_OPTIONS)}
                control={control}
                errors={errors}
                columns={2}
                setValue={setValue}
              />
            </div>
          </SectionCard>

          {/* REPLACEMENT - Commented out for future management
          <SectionCard
            icon="ri-user-shared-line"
            title="Replacement"
            subtitle="Specify whether a replacement subject is required"
          >
            <CommonForm
              fields={getReplacementFields(replacementRequired)}
              control={control}
              errors={errors}
              columns={2}
              setValue={setValue}
            />
          </SectionCard>
          */}

          {/* SUPPORTING DOCUMENTS */}
          <SectionCard
            icon="ri-attachment-2"
            title="Supporting Documents"
            subtitle="Upload medical reports, consent forms, AE reports and other relevant documents"
          >
            {/* File Upload Zone */}
            <div className="mb-4">
              <CommonFileProgress
                files={uploadedFiles}
                existingSizeBytes={existingAttachments.reduce((sum, f) => sum + parseSizeToBytes(f.size), 0)}
                maxSizeMB={20}
              />
              <div
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                style={{
                  border: `1px dashed ${isDragging ? "#2E86AB" : "#d0d5dd"}`,
                  borderRadius: 8,
                  padding: "40px 20px",
                  textAlign: "center",
                  cursor: "pointer",
                  background: isDragging ? "#f0f7ff" : "#fafbfc",
                  transition: "all 0.2s",
                }}
              >
                <i
                  className="ri-upload-cloud-2-line text-primary"
                  style={{ fontSize: 32, display: "block", marginBottom: 8 }}
                />
                <p className="mb-1 fw-bold text-dark" style={{ fontSize: 14 }}>
                  Drag & drop files here, or click to browse
                </p>
                <p className="text-muted mb-3" style={{ fontSize: 12 }}>
                  Accepted: PDF, JPG, PNG, DOCX - Max 10MB per file
                </p>
                <Button
                  type="button"
                  color="light"
                  size="sm"
                  className="border px-3"
                  onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                >
                  Browse Files
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png,.docx"
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                />
              </div>

              {/* Existing attachments from server */}
              {existingAttachments.length > 0 && (
                <div className="mt-3 d-flex flex-column gap-2">
                  <p className="text-muted small fw-medium mb-1">Existing Attachments</p>
                  <Row className="gap-2 px-0 w-100 mx-auto">
                    {existingAttachments.map((file, idx) => (
                      <Col
                        key={`existing-${idx}`}
                        md={4}
                        sm={12}
                        className={`px-0 ${addVolunteerStyle.ipInventory_wrapper}`}
                      >
                        <div className={`${addVolunteerStyle.attachmentCard} h-100`}>
                          <div className={addVolunteerStyle.fileInfo}>
                            <div className={addVolunteerStyle.fileIcon}>
                              <i className="ri-file-pdf-line text-danger" />
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div className={addVolunteerStyle.fileName}>
                                <TruncatedText text={file.filename} />
                              </div>
                              <div className={addVolunteerStyle.fileMeta}>
                                {file.size}
                              </div>
                            </div>
                          </div>
                          <div className={addVolunteerStyle.fileActions}>
                            <i
                              className="ri-eye-line text-primary"
                              title="Preview"
                              style={{ cursor: "pointer", marginRight: "10px" }}
                              onClick={() => handlePreview(file)}
                            />
                            <i
                              className="ri-delete-bin-line text-danger"
                              title="Delete"
                              style={{ cursor: "pointer" }}
                              onClick={() => handleDeleteExisting(file.dropoutattachmentid)}
                            />
                          </div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </div>
              )}

              {/* Newly uploaded files */}
              {uploadedFiles.length > 0 && (
                <div className="mt-3 d-flex flex-column gap-2">
                  <p className="text-muted small fw-medium mb-1">New Attachments</p>
                  <Row className="gap-2 px-0 w-100 mx-auto">
                    {uploadedFiles.map((file, idx) => (
                      <Col
                        key={idx}
                        md={4}
                        sm={12}
                        className={`px-0 ${addVolunteerStyle.ipInventory_wrapper}`}
                      >
                        <div className={`${addVolunteerStyle.attachmentCard} h-100`}>
                          <div className={addVolunteerStyle.fileInfo}>
                            <div className={addVolunteerStyle.fileIcon}>
                              <i className="ri-file-pdf-line text-danger" />
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div className={addVolunteerStyle.fileName}>
                                <TruncatedText text={file.name} />
                              </div>
                              <div className={addVolunteerStyle.fileMeta}>
                                {(file.size / 1024).toFixed(2)} KB
                              </div>
                            </div>
                          </div>
                          <div className={addVolunteerStyle.fileActions}>
                            <i
                              className="ri-eye-line text-primary"
                              title="Preview"
                              onClick={(e) => { e.stopPropagation(); handlePreview(file); }}
                              style={{ cursor: "pointer", marginRight: "10px" }}
                            />
                            <i
                              className="ri-delete-bin-line text-danger"
                              title="Delete"
                              onClick={(e) => { e.stopPropagation(); removeFile(idx); }}
                              style={{ cursor: "pointer" }}
                            />
                          </div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </div>
              )}
            </div>
          </SectionCard>

          {/* Sticky Footer */}
          <div className={formStyles.stickyFooter}>
            <div className={formStyles.footerActions}>
              <Button type="button" color="dark" outline onClick={() => {
                handleClear();
                if (editId) {
                  navigate(DROPOUT_VOLUNTEER_PATHS.LIST);
                } else {
                  navigate(SUBJECT_PATHS.INFO_SUBJECT_ACTION);
                }
              }}>
                Cancel
              </Button>
              <div className="d-flex gap-2">
                <Button type="submit" color="primary" disabled={InsertMutation.isPending || UpdateMutation.isPending}>
                  {InsertMutation.isPending ? <Spinner size="sm" className="me-2" /> : <i className="ri-check-line me-1" />}
                  {editId ? "Update" : "Submit"} Dropout Record
                </Button>
              </div>
            </div>
          </div>
        </form>

        {/* File Preview Modal */}
        <FilePreviewModal
          file={previewFile}
          fileUrl={previewFileUrl}
          isOpen={previewOpen}
          onClose={() => setPreviewOpen(false)}
        />

        {isReasonModal && (
          <Reason
            isOpen={isReasonModal}
            toggle={() => setIsReasonModal(!isReasonModal)}
            action="Update"
            id={Number(editId)}
            click={btnReasonClick}
            isLoading={UpdateMutation.isPending}
          />
        )}
      </Container>
    </div>
  );
};

export default AddDropoutVolunteer;
